import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { FirestoreDB } from '../utils/firebase/firebase';
import AuthContext from './AuthContext';
import { fetchUserNotificationMessages } from '../api/feedback';

const NotificationContext = createContext();

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    // Return default values instead of throwing error
    return {
      notifications: [],
      unreadCount: 0,
      isLoading: false,
      refetchNotifications: () => {},
      fetchNotifications: () => {},
    };
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const userId = user?.uid;

  // Add unique instance ID to track multiple providers
  // const instanceId = useRef(Math.random().toString(36).substr(2, 9));

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const unsubscribeRef = useRef(null);
  const lastFetchRef = useRef(0);
  const fetchCountRef = useRef(0);
  const isListenerSetUpRef = useRef(false);

  const fetchNotifications = async (
    forceRefresh = false,
    source = 'unknown'
  ) => {
    if (!userId) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    // Prevent multiple simultaneous fetches
    if (isFetching) {
      console.log(`Already fetching notifications, skipping...`);
      return;
    }

    // Only fetch if we haven't fetched in the last 60 seconds
    const now = Date.now();
    if (!forceRefresh && now - lastFetchRef.current < 60000 && isInitialized) {
      return;
    }

    fetchCountRef.current++;
    setIsFetching(true);
    setIsLoading(true);

    try {
      const messages = await fetchUserNotificationMessages(userId);
      const unreadMessages = messages.filter(
        (message) => !message.readStatus && message.title
      );

      setNotifications(messages || []);
      setUnreadCount(unreadMessages.length || 0);
      lastFetchRef.current = now;
      setIsInitialized(true);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setIsLoading(false);
      setIsFetching(false);
    }
  };

  const refetchNotifications = async () => {
    await fetchNotifications(true, 'Manual'); // Force refresh
  };

  // Set up Firestore listener only
  useEffect(() => {
    if (!userId) {
      // Clean up if no user
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      setNotifications([]);
      setUnreadCount(0);
      setIsInitialized(false);
      lastFetchRef.current = 0;
      fetchCountRef.current = 0;
      isListenerSetUpRef.current = false;
      return;
    }

    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    // Initial fetch only once
    fetchNotifications(false, 'Initial');

    // Set up Firestore listener
    const notificationsRef = collection(
      FirestoreDB,
      'notifications',
      userId,
      'messages'
    );

    unsubscribeRef.current = onSnapshot(
      notificationsRef,
      async (snapshot) => {
        // Skip the initial trigger when listener is first set up
        if (!isListenerSetUpRef.current) {
          isListenerSetUpRef.current = true;
          return;
        }

        // Fetch latest notifications when snapshot changes (real-time update)
        await fetchNotifications(true, 'snapshot');
      },
      (error) => {
        console.error('Error listening to notifications:', error);
      }
    );

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      isListenerSetUpRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, []);

  const value = {
    notifications: notifications || [],
    unreadCount: unreadCount || 0,
    isLoading: isLoading || false,
    refetchNotifications,
    fetchNotifications,
    isInitialized,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
